// The words of an MCP client's sign-in pages (spec platform/mcp-oauth, PR 4): the consent and the
// problem page. Live has no OAuth sign-in, so the words are ours, in live's sign-in card.

/** The access a person may give, named as Settings' tokens name it. */
export const ACCESS = {
  write: { label: "Read and write", hint: "It can start generations and change files, as you can." },
  read: { label: "Read only", hint: "It can look, and never change anything." },
} as const;

export const CONSENT = {
  reading: "Connect an app to Created.ai",
  title: (client: string) => `Connect ${client} to Created.ai`,
  asks: (client: string, host: string) =>
    `${client}, from ${host}, asks to work as you in one of your teams.`,
  back: (host: string) => `When you answer, you go back to ${host}.`,
  loopback: (client: string, host: string) =>
    `${client} signs in through ${host}, on this computer, where any app could ask the same. ` +
    `Allow it only if you just started connecting ${client} yourself.`,
  team: "Team",
  access: "Access",
  allow: "Allow",
  cancel: "Cancel",
  note: "Settings, Access tokens lists the connection, and Revoke ends it. It ends by itself after 90 days.",
  noTeam: "You are in no team yet. Make one, then connect the app again.",
  makeTeam: "Make a team",
  ended: "This request has ended",
  endedText:
    "A request lasts 10 minutes and works once, in the browser that started it. Start connecting " +
    "again from the app.",
} as const;

/** Why `/api/oauth/authorize` could not prove the app: its `reason`s (spec decision 22). */
const REASONS: Readonly<Record<string, string>> = {
  client_id: "It did not name itself in a way Created.ai accepts.",
  client_document: "Created.ai could not read or prove the app's description of itself.",
  redirect_uri: "It asked to send you back to an address its description does not list.",
  repeated: "Its request named the app or the way back more than once.",
};

export const PROBLEM = {
  title: "This app could not connect",
  unknown: "Its request could not be checked.",
  nothing: "Nothing was shared with it. Close this tab, or tell the app's makers what this page says.",
  home: "Go to Created.ai",
} as const;

/** The problem page's words for a `reason`: a reason it does not know reads as unchecked. */
export function problemOf(reason: string | undefined): string {
  return reason !== undefined && Object.hasOwn(REASONS, reason)
    ? (REASONS[reason] ?? PROBLEM.unknown)
    : PROBLEM.unknown;
}
